import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";

const PlanForm: React.FC = () => {
 const { t } = useTranslation();
 const { planId } = useParams<{ planId: string }>();
 const navigate = useNavigate();
 const { isAuthenticated } = usePlatformAuth();
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [formData, setFormData] = useState({
  name: "",
  description: "",
  price: "",
  yearlyPrice: "",
  billingCycle: "both" as "monthly" | "yearly" | "both",
  features: [""],
  limits: {
   products: "",
   orders: "",
   pages: "",
   categories: "",
   users: "",
   mediaSpace: "", // Media space in MB (converted to bytes on submit)
  },
  isActive: true,
 });

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (planId && planId !== "new") {
   fetchPlan();
  }
 }, [planId, isAuthenticated, navigate]);

 const fetchPlan = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await platformApiService.getPlans();
   const plans = response.data.plans || [];
   const plan = plans.find((p: any) => p._id === planId);
   if (plan) {
    setFormData({
     name: plan.name || "",
     description: plan.description || "",
     price: plan.price?.toString() || "",
     yearlyPrice: plan.yearlyPrice?.toString() || "",
     billingCycle: plan.billingCycle || "both",
     features: plan.features && plan.features.length > 0 ? plan.features : [""],
     limits: {
      products: plan.limits?.products?.toString() || "",
      orders: plan.limits?.orders?.toString() || "",
      pages: plan.limits?.pages?.toString() || "",
      categories: plan.limits?.categories?.toString() || "",
      users: plan.limits?.users?.toString() || "",
      mediaSpace: plan.limits?.mediaSpace ? (plan.limits.mediaSpace / (1024 * 1024)).toString() : "", // Convert bytes to MB
     },
     isActive: plan.isActive !== undefined ? plan.isActive : true,
    });
   }
  } catch (err: any) {
   console.error("Error fetching plan:", err);
   setError(err.response?.data?.error || "Failed to fetch plan");
  } finally {
   setLoading(false);
  }
 };

 const handleFeatureChange = (index: number, value: string) => {
  const newFeatures = [...formData.features];
  newFeatures[index] = value;
  setFormData({ ...formData, features: newFeatures });
 };

 const addFeature = () => {
  setFormData({ ...formData, features: [...formData.features, ""] });
 };

 const removeFeature = (index: number) => {
  const newFeatures = formData.features.filter((_, i) => i !== index);
  setFormData({ ...formData, features: newFeatures.length > 0 ? newFeatures : [""] });
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setLoading(true);

  try {
   const submitData: any = {
    name: formData.name,
    description: formData.description,
    price: parseFloat(formData.price),
    billingCycle: formData.billingCycle,
    features: formData.features.filter((f) => f.trim() !== ""),
    limits: {
     ...(formData.limits.products && { products: parseInt(formData.limits.products) }),
     ...(formData.limits.orders && { orders: parseInt(formData.limits.orders) }),
     ...(formData.limits.pages && { pages: parseInt(formData.limits.pages) }),
     ...(formData.limits.categories && { categories: parseInt(formData.limits.categories) }),
     ...(formData.limits.users && { users: parseInt(formData.limits.users) }),
     ...(formData.limits.mediaSpace && { mediaSpace: Math.round(parseFloat(formData.limits.mediaSpace) * 1024 * 1024) }), // Convert MB to bytes
    },
    isActive: formData.isActive,
   };

   if (formData.yearlyPrice) {
    submitData.yearlyPrice = parseFloat(formData.yearlyPrice);
   }

   if (planId && planId !== "new") {
    submitData.id = planId;
    await platformApiService.updatePlan(planId, submitData);
   } else {
    await platformApiService.upsertPlan(submitData);
   }
   navigate("/platform/plans");
  } catch (err: any) {
   console.error("Error saving plan:", err);
   setError(err.response?.data?.error || "Failed to save plan");
  } finally {
   setLoading(false);
  }
 };

 if (loading && planId && planId !== "new") {
  return <div className="p-8">{t("platform.planForm.loading")}</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">
     {planId && planId !== "new" ? t("platform.planForm.editPlan") : t("platform.planForm.createNewPlan")}
    </h1>
    <button
     onClick={() => navigate("/platform/plans")}
     className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
    >
     {t("platform.planForm.backToPlans")}
    </button>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
     {error}
    </div>
   )}

   <div className="bg-white shadow p-6">
    <form onSubmit={handleSubmit} className="space-y-6">
     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("platform.planForm.name")} <span className="text-red-500">*</span>
      </label>
      <input
       type="text"
       required
       value={formData.name}
       onChange={(e) => setFormData({ ...formData, name: e.target.value })}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       placeholder={t("common.placeholders.basicPlanExample")}
      />
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("platform.planForm.description")} <span className="text-red-500">*</span>
      </label>
      <textarea
       required
       value={formData.description}
       onChange={(e) => setFormData({ ...formData, description: e.target.value })}
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       rows={3}
       placeholder={t("platform.planForm.planDescription")}
      />
     </div>

     <div className="grid grid-cols-2 gap-4">
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        {t("platform.planForm.monthlyPrice")} <span className="text-red-500">*</span>
       </label>
       <input
        type="number"
        step="0.01"
        min="0"
        required
        value={formData.price}
        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       />
      </div>

      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        {t("platform.planForm.yearlyPrice")} <span className="text-gray-500">{t("platform.planForm.optional")}</span>
       </label>
       <input
        type="number"
        step="0.01"
        min="0"
        value={formData.yearlyPrice}
        onChange={(e) => setFormData({ ...formData, yearlyPrice: e.target.value })}
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
       />
      </div>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("platform.planForm.billingCycle")} <span className="text-red-500">*</span>
      </label>
      <select
       value={formData.billingCycle}
       onChange={(e) =>
        setFormData({ ...formData, billingCycle: e.target.value as any })
       }
       className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
       <option value="monthly">{t("platform.planForm.monthlyOnly")}</option>
       <option value="yearly">{t("platform.planForm.yearlyOnly")}</option>
       <option value="both">{t("platform.planForm.bothMonthlyAndYearly")}</option>
      </select>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       Features
      </label>
      {formData.features.map((feature, index) => (
       <div key={index} className="flex gap-2 mb-2">
        <input
         type="text"
         value={feature}
         onChange={(e) => handleFeatureChange(index, e.target.value)}
         className="flex-1 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.featureDescription")}
        />
        <button
         type="button"
         onClick={() => removeFeature(index)}
         className="px-3 py-2 bg-red-100 text-red-700 hover:bg-red-200"
        >
         {t("platform.planForm.remove")}
        </button>
       </div>
      ))}
      <button
       type="button"
       onClick={addFeature}
       className="mt-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200"
      >
       {t("platform.planForm.addFeature")}
      </button>
     </div>

     <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
       {t("platform.planForm.planLimits")}
      </label>
      <div className="grid grid-cols-2 gap-4">
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.products")}</label>
        <input
         type="number"
         min="0"
         value={formData.limits.products}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, products: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
       </div>
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.orders")}</label>
        <input
         type="number"
         min="0"
         value={formData.limits.orders}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, orders: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
       </div>
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.pages")}</label>
        <input
         type="number"
         min="0"
         value={formData.limits.pages}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, pages: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
       </div>
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.categories")}</label>
        <input
         type="number"
         min="0"
         value={formData.limits.categories}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, categories: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
       </div>
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.users")}</label>
        <input
         type="number"
         min="0"
         value={formData.limits.users}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, users: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
       </div>
       <div>
        <label className="block text-xs text-gray-500 mb-1">{t("platform.planForm.mediaSpace")} (MB)</label>
        <input
         type="number"
         step="0.01"
         min="0"
         value={formData.limits.mediaSpace}
         onChange={(e) =>
          setFormData({
           ...formData,
           limits: { ...formData.limits, mediaSpace: e.target.value },
          })
         }
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
         placeholder={t("platform.planForm.unlimited")}
        />
        <p className="text-xs text-gray-400 mt-1">{t("platform.planForm.mediaSpaceHint")}</p>
       </div>
      </div>
     </div>

     <div>
      <label className="flex items-center gap-2">
       <input
        type="checkbox"
        checked={formData.isActive}
        onChange={(e) =>
         setFormData({ ...formData, isActive: e.target.checked })
        }
        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
       />
       <span className="text-sm font-medium text-gray-700">Active</span>
      </label>
     </div>

     <div className="flex gap-2">
      <button
       type="submit"
       disabled={loading}
       className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
       {loading ? "Saving..." : planId && planId !== "new" ? "Update Plan" : "Create Plan"}
      </button>
      <button
       type="button"
       onClick={() => navigate("/platform/plans")}
       className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
       Cancel
      </button>
     </div>
    </form>
   </div>
  </div>
 );
};

export default PlanForm;





